/** Pure helpers for the issue action-log timeline. Kept hook-free for unit testing. */

export type ChangedField = {
  old_value?: unknown
  new_value?: unknown
}

export type ChangedFields = Record<string, ChangedField>

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/

/** Pull `changed_fields` out of the JSONString metadata. Null if absent/invalid. */
export function parseChangedFields(
  metadata?: string | null
): ChangedFields | null {
  if (!metadata) return null
  try {
    const parsed = JSON.parse(metadata)
    const fields = parsed?.changed_fields
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      return fields as ChangedFields
    }
  } catch {
    // fall through
  }
  return null
}

function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value)
}

type FormatOpts = {
  yes: string
  no: string
  /** Formats an ISO date string for display. */
  formatDate: (iso: string) => string
}

/** Render a stored field value as a string, or null when empty (shown as "—"). */
export function formatValue(raw: unknown, opts: FormatOpts): string | null {
  if (raw === null || raw === undefined || raw === '') return null

  // Relation: { id, name }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const name = (raw as { name?: unknown }).name
    return typeof name === 'string' && name ? name : null
  }

  // Many-to-many: array of { id, name }
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null
    const names = raw
      .map(v =>
        v && typeof v === 'object'
          ? ((v as { name?: string }).name ?? '')
          : String(v)
      )
      .filter(Boolean)
    return names.length > 0 ? names.join(', ') : null
  }

  if (typeof raw === 'boolean') return raw ? opts.yes : opts.no

  if (typeof raw === 'string' && isIsoDate(raw)) {
    return opts.formatDate(raw)
  }

  return String(raw)
}

/** Humanize an unmapped model field key: `responsible_user_id` -> "Responsible user". */
export function humanizeFieldKey(key: string): string {
  const cleaned = key.replace(/_id$/, '').replace(/_/g, ' ')
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}
